/**
 * VacationAccountModel.js
 * Defines the structure and types for employee vacation accounts per year
 */

// Adjustment types enum
export const AdjustmentType = {
  BONUS: 'bonus',
  REDUCTION: 'reduction',
  CARRYOVER_CORRECTION: 'carryover_correction',
  ADMIN_ADJUSTMENT: 'admin_adjustment',
  TRANSFER: 'transfer'
};

// Adjustment labels for UI (will be translated via i18n)
export const AdjustmentTypeLabels = {
  [AdjustmentType.BONUS]: 'Bonus Days',
  [AdjustmentType.REDUCTION]: 'Reduction',
  [AdjustmentType.CARRYOVER_CORRECTION]: 'Carryover Correction',
  [AdjustmentType.ADMIN_ADJUSTMENT]: 'Admin Adjustment',
  [AdjustmentType.TRANSFER]: 'Transfer'
};

/**
 * Vacation Account Adjustment structure
 */
export class VacationAdjustment {
  constructor({
    type = AdjustmentType.ADMIN_ADJUSTMENT,
    days = 0,
    note = '',
    createdAt = new Date(),
    createdBy = ''
  } = {}) {
    this.type = type;
    this.days = days; // Can be positive or negative
    this.note = note;
    this.createdAt = createdAt instanceof Date ? createdAt : new Date(createdAt);
    this.createdBy = createdBy;
  }

  toFirestore() {
    return {
      type: this.type,
      days: this.days,
      note: this.note,
      createdAt: this.createdAt,
      createdBy: this.createdBy
    };
  }

  static fromFirestore(data) {
    return new VacationAdjustment({
      ...data,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
    });
  }
}

/**
 * Vacation Account structure for a specific year
 */
export class VacationAccount {
  constructor({
    year = new Date().getFullYear().toString(),
    baseEntitlement = 0,
    carryOver = 0,
    carryOverLimit = 0,
    carryOverDeadline = null, // Default March 31
    adjustments = [],
    totalEntitlement = 0,
    usedDays = 0,
    remainingDays = 0,
    createdAt = new Date(),
    updatedAt = new Date()
  } = {}) {
    this.year = year;
    this.baseEntitlement = baseEntitlement;
    this.carryOver = carryOver;
    this.carryOverLimit = carryOverLimit;
    this.carryOverDeadline = carryOverDeadline || this.getDefaultDeadline(year);
    this.adjustments = adjustments.map(adj => 
      adj instanceof VacationAdjustment ? adj : new VacationAdjustment(adj)
    );
    this.totalEntitlement = totalEntitlement;
    this.usedDays = usedDays;
    this.remainingDays = remainingDays;
    this.createdAt = createdAt instanceof Date ? createdAt : new Date(createdAt);
    this.updatedAt = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
  }

  /**
   * Get default carryover deadline (March 31 of the year)
   */
  getDefaultDeadline(year) {
    return new Date(`${year}-03-31`);
  }

  /**
   * Calculate total adjustments (sum of all adjustment days)
   */
  getTotalAdjustments() {
    return this.adjustments.reduce((sum, adj) => sum + adj.days, 0);
  }

  /**
   * Recalculate all totals based on current values
   */
  recalculate(usedDays = null) {
    const totalAdjustments = this.getTotalAdjustments();
    this.totalEntitlement = this.baseEntitlement + this.carryOver + totalAdjustments;
    
    if (usedDays !== null) {
      this.usedDays = usedDays;
    }
    
    this.remainingDays = this.totalEntitlement - this.usedDays;
    this.updatedAt = new Date();
  }

  /**
   * Check if account has sufficient days for a vacation request
   */
  hasSufficientDays(requestedDays) {
    return this.remainingDays >= requestedDays;
  }

  /**
   * Check if carryover has expired (past deadline)
   */
  isCarryOverExpired() {
    if (!this.carryOverDeadline || this.carryOver <= 0) {
      return false;
    }
    const deadline = this.carryOverDeadline instanceof Date 
      ? this.carryOverDeadline 
      : new Date(this.carryOverDeadline);
    return new Date() > deadline;
  }

  /**
   * Get remaining carryover days (0 if expired)
   */
  getRemainingCarryOver() {
    return this.isCarryOverExpired() ? 0 : this.carryOver;
  }

  /**
   * Convert to Firestore document format
   */
  toFirestore() {
    return {
      year: this.year,
      baseEntitlement: this.baseEntitlement,
      carryOver: this.carryOver,
      carryOverLimit: this.carryOverLimit,
      carryOverDeadline: this.carryOverDeadline,
      adjustments: this.adjustments.map(adj => adj.toFirestore()),
      totalEntitlement: this.totalEntitlement,
      usedDays: this.usedDays,
      remainingDays: this.remainingDays,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  /**
   * Create instance from Firestore document
   */
  static fromFirestore(data) {
    return new VacationAccount({
      ...data,
      carryOverDeadline: data.carryOverDeadline?.toDate 
        ? data.carryOverDeadline.toDate() 
        : data.carryOverDeadline 
        ? new Date(data.carryOverDeadline)
        : null,
      adjustments: (data.adjustments || []).map(adj => VacationAdjustment.fromFirestore(adj)),
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
    });
  }

  /**
   * Create a default vacation account for a year
   */
  static createDefault(year, baseEntitlement = 0, previousYearCarryOver = 0, carryOverLimit = 0) {
    const account = new VacationAccount({
      year: year.toString(),
      baseEntitlement,
      carryOver: Math.min(previousYearCarryOver, carryOverLimit), // Apply limit
      carryOverLimit,
      adjustments: [],
      usedDays: 0
    });
    account.recalculate();
    return account;
  }
}

export default VacationAccount;